import React from 'react';
import { NextPage } from 'next';
import Layout from '../../components/layout';
import Link from '../../components/link';
import GenericContainer from '../../components/@container/generic-container';
import useCustomer from '../../hooks/use-customer';
import Button from '../../components/button';

const RecallComplianceLetterPage: NextPage = () => {
  const customer = useCustomer();

  return (
    <Layout title="Recall Compliance Letter">
      <GenericContainer
        title="Volvo Recall Compliance Clearance Letter Process"
        breadcrumb={{ url: '/owners', text: 'Back' }}
      >
        <div className="content">
          <div className="warning-notice">
            <p>
              Volvo charges $1000 USD for issuance of the recall compliance
              letter. This is due to costs for vehicle analytical research
              investigation, and other internal vehicle validation processes,
              thereby allowing Volvo to issue the recall compliance clearance
              letter in a timely manner. Payment using non-US funds may incur
              additional currency exchange fees from your credit card company.
            </p>
            <p style={{ marginTop: '1rem' }}>
              Please note that Model Year 2013 and earlier vehicles may be
              limited due to parts availability. Please contact your Volvo
              retailer prior to purchase.
            </p>
          </div>
          {customer?.customer?.guest ? (
            <div className="info-notice">
              <p>
                To create a new request, you must be logged into an account.
                Please sign in or create a new free account and try again. After
                logging in, you can access this page under the resources tab.
              </p>
            </div>
          ) : (
            <Button
              text="Request recall compliance letter"
              onClick={() =>
                (window.location.href = '/account/compliance-letter/new')
              }
            />
          )}
          <h3>General Information:</h3>
          <h4>Order process:</h4>
          <p>
            After reviewing all of the information on this page, Click on the
            Request recall Compliance letter button below and enter your VIN
            number - this must be a VIN number for a US or Canadian vehicle
            only. When you submit, the next screen will Validate your VIN and
            when you click on proceed, a request will be sent to Volvo. To view
            any open requests, you can go to your account (from the menu in the
            upper right corner) and click on the Compliance Letter section.
          </p>
          <p>
            You will receive a notification after Volvo has reviewed your
            request - if there are any recalls or issues, you will need to
            resolve them before proceeding. If the VIN number clears all of the
            checks, you will be prompted at that time to purchase the recall
            compliance letter. This is available from a link in the request
            system - which is accessible from a link in the email or from your
            account (from the menu in the upper right corner) in the Compliance
            Letter section.
          </p>
          <p>
            After purchasing the letter you will be notified by email when the
            letter is available - again, this will be available as a PDF from a
            link in the email or from your account (from the menu in the upper
            right corner) in the Compliance Letter section.
          </p>
          <p>
            In order for your local Volvo retailer to begin updating your
            vehicle (if necessary) the Recall compliance letter must be
            purchased prior to your appointment. Be sure to bring the letter
            with you to your appointment. It is recommended to call the service
            department prior to your appointment should you have any questions
          </p>
          <p>
            As part of the import or export process of your Volvo vehicle,
            government authorities will require a compliance letter as proof
            that your vehicle conforms to emission and safety standards. Volvo
            will provide customers moving to the U.S./or Canada with a
            compliance letter upon submitting their vehicle information on this
            website and the appropriate fee has been paid. Compliance letters
            can typically take approximately 5-10 business days to be prepared,
            and you will be notified when it is available on the site.
          </p>
          <p>
            Please note that for U.S. customers moving to Canada, please consult
            the Registrar of Imported Vehicles at this website{' '}
            <Link href="https://www.riv.ca/Home.aspx">
              https://www.riv.ca/Home.aspx
            </Link>{' '}
            for a list of requirements and necessary steps before importing your
            vehicle into Canada.
          </p>
          <p>
            <em>
              Additional information on importing vehicles purchased in the
              United States to Canada (refer to link provided below for general
              topic information):
            </em>
          </p>
          <p>
            <Link href="https://www.tc.gc.ca/en/services/road/importing-vehicle.html">
              https://www.tc.gc.ca/en/services/road/importing-vehicle.html
            </Link>
          </p>
          <h4>
            Please note the following From:{' '}
            <Link href="https://www.tc.gc.ca/en/transport-canada.html">
              Transport Canada
            </Link>
          </h4>
          <p>
            Did you know that some vehicles that are sold or manufactured in the
            United States cannot be imported into Canada?
          </p>
          <p>
            If you are thinking about buying a vehicle in the U.S., please visit
            the{' '}
            <Link href="https://www.riv.ca/Home.aspx">
              Registrar of Imported Vehicles
            </Link>
            web site for information and the required import e-form.
          </p>
          <p>
            You should also refer to the{' '}
            <Link href="https://www.tc.gc.ca/en/services/road/importing-vehicle/purchased-united-states/vehicle-import-compatibility-list.html">
              Vehicle Import Compatibility list
            </Link>{' '}
            to check which Volvo vehicles are admissible for import from the
            U.S.
          </p>
          <div className="notice">
            <p>
              You cannot import a vehicle with an open recall into Canada from
              the United States, until the recall has been performed. Check for
              any open recalls on your vehicle by using this{' '}
              <Link href="https://www.volvocars.com/us/own/additional-choices/recall-information">
                Recall Search Tool Link
              </Link>
            </p>
          </div>
          <h3>Taxes & Duties:</h3>
          <p>
            You must pay taxes and/or duties, and bring a vehicle through
            customs, before it can be inspected for importation into Canada.
            Vehicles that fail the inspection, or that are not clear of recalls,
            cannot remain in Canada, even though taxes and duties would have
            been paid.
          </p>
          <p>
            If you are a visitor or temporary resident bringing your vehicle
            into Canada, please read{' '}
            <Link href="https://www.tc.gc.ca/en/services/road/importing-vehicle/temporarily/visitors-tourists-temporary-residents-entering-canada-foreign-owned-vehicles.html">
              Information for visitors, tourists, and temporary residents
              entering Canada with foreign-owned vehicles
            </Link>
            .
          </p>
          <h3>Canadian Vehicles Going To The U.S.:</h3>
          <p>
            A Canadian Volvo is deemed inadmissible for exportation to the U.S.
            when:
          </p>
          <ol>
            <li>
              It cannot be retrofitted to meet U.S. Federal Motor Vehicle Safety
              Standards (FMVSS) or;
            </li>
            <li>
              Is not available in the U.S. market, therefore considered a
              &quot;Canada unique model&quot; and is less than 25 years old.
            </li>
          </ol>
          <p>
            Although similar, Canadian & U.S. safety standards are not
            completely aligned on a one to one basis. Therefore, some Canadian
            vehicles may require certification work to be performed in order for
            your vehicle to comply with the applicable U.S. FMVSS.{' '}
            <em>
              Please note that these are regulatory requirements established by
              the U.S. NHTSA and DOT government agencies, and not Volvo.
            </em>
          </p>
          <p>
            Volvo will issue an email (refer to email request for recall
            compliance clearance letter below) to you stating whether any
            certification work is necessary to bring the vehicle into compliance
            with any applicable U.S. FMVSS (as doing this can be expensive or in
            some cases cannot be accomplished technically). Certification work
            may be performed at a Volvo authorized dealership. However, a
            compliance letter stating that the vehicle has been brought into
            conformity with U.S. FMVSS and is clear of any recalls will only be
            issued by Volvo if the required certification work has been
            performed at an authorized Volvo dealer (if necessary) and our
            vehicle investigation has been completed and the appropriate recall
            compliance clearance letter fee has been paid.
          </p>
          <h3>Important Information on Costs:</h3>
          <p>
            Cost estimates for parts and labor for any certification work are
            only available through an authorized Volvo dealer. The scope of the
            safety certification work required varies depending on the model
            and/or model year. Please request a letter of compliance by
            submitting/completing the online form on this website. Our email
            will outline the certification work required (if any) to bring your
            Canadian vehicle into compliance with any applicable U.S.FMVSS.
          </p>
          <h4>
            Recall Clearance Compliance Letters for Canadian Importation of U.S.
            Volvo Vehicles.
          </h4>
          <p>
            Recall clearance compliance letters for Canadian vehicles must be
            obtained through this website. Please submit your request by
            completing the online form on this website and stating in the
            comments section that you require a Recall Compliance Clearance
            Letter.
          </p>
          <p>
            Many Canadian vehicles have emission control systems that are
            identical to the emission control systems on Volvo&apos;s certified
            in the US. However, although they are identical to a certified
            configuration, these Canadian vehicles may not have an emissions
            compliance label identifying them as conforming to EPA requirements.
          </p>
          <h4>A Canadian Vehicle is identical to a US Certified Version if:</h4>
          <div className="or-notice">
            <p>
              The Canadian vehicle has an emission label stating it is certified
              to applicable US EPA federal and/or 50-State emission standards.{' '}
              <Link href="https://www.epa.gov/importing-vehicles-and-engines/locating-vehicle-emissions-label">
                How to Find the Vehicle Emissions Label.
              </Link>
            </p>
            <p>
              Volvo has provided a letter of compliance that states the vehicle
              complies with all applicable US EPA and/or 50-State emission
              regulations.
            </p>
          </div>
          <p>
            Additionally,{' '}
            <Link href="https://www.cbp.gov/trade/basic-import-export">
              U.S. Customs and Border Protection (CBP)
            </Link>{' '}
            may request a letter of compliance from the manufacturer (from
            Volvo), even if the vehicle is determined by EPA to be identical to
            a U.S. certified version. Reference the link provided above for
            specific details and complete information related to the CBP
            processes.
          </p>
          <p>
            Please note that you will also need to contact the{' '}
            <Link href="https://www.nhtsa.gov/importing-vehicle">
              National Highway Traffic Safety Administration (NHTSA)
            </Link>
            for NHTSA applicable import regulations (click on the link provided
            above for specific information and details).
          </p>
          <h4>
            If the Canadian Vehicle is not identical to a U.S. Certified
            Version, please refer to:
          </h4>
          <ul>
            <li>
              Section 2.1.2.2, &quot;Canadian Vehicles Not Identical to United
              States Version Vehicles&quot;
            </li>
            <li>
              Section 2.1.3, &quot;Non-United States Version Vehicles&quot;, in
              the{' '}
              <Link href="https://nepis.epa.gov/Exe/ZyPdf.cgi?Dockey=P10081IS.pdf">
                Procedures for Importing Vehicles and Engines Manual
              </Link>{' '}
              (68 pp, 3.23 MB,, EPA-420-B-10-027,{' '}
              <Link href="https://www.epa.gov/home/pdf-files">About PDF</Link>).
            </li>
          </ul>
        </div>
      </GenericContainer>
      {/* language=CSS */}
      <style jsx>{`
        .content {
          font-family: 'Volvo Novum', sans-serif;
        }

        .content p {
          margin: 1rem 0 0 0;
        }

        .warning-notice {
          color: #a94442;
          background-color: #f2dede;
          padding: 1rem;
        }

        .warning-notice p {
          margin: 0;
        }

        .info-notice {
          color: #fff;
          background-color: #003057;
          padding: 1rem;
          margin-top: 1rem;
        }

        .info-notice p {
          margin: 0;
        }

        .notice {
          background-color: #ddd;
          padding: 1rem;
          margin-top: 1rem;
        }

        .notice p {
          margin: 0;
        }

        .or-notice {
          background-color: #ddd;
          padding: 1rem;
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          gap: 1rem;
        }

        .or-notice p {
          margin: 0;
        }

        h2,
        h3,
        h4,
        strong {
          font-weight: 500;
        }

        .content a,
        .content :global(a) {
          color: #333;
        }

        .content a:hover,
        .content :global(a:hover) {
          color: #707070;
        }
      `}</style>
    </Layout>
  );
};

export default RecallComplianceLetterPage;
